import type { Task } from "../core/types.js";

// CYBORG-1176 — the one owner of "load the task board". The board loads in one call
// at workspace open; every later caller (Tasks visit, reconnect, pull-to-refresh)
// either joins the load already running or asks only for what changed since.

export interface TaskBoardChanges {
  tasks: Task[];
  ids: string[];
  asOf: number;
}

export interface TaskBoardSyncDeps {
  // onPartial: the board so far, after each page but the last (CYBORG-1206), so the
  // first rows render while the rest are still arriving.
  fetchBoard(
    wsId: string,
    onPartial: (tasksSoFar: Task[]) => void,
  ): Promise<{ tasks: Task[]; asOf: number | null }>;
  // heldIds: the ids the board holds now, so the relay can skip resending an
  // unchanged visible set (CYBORG-1198).
  fetchChanges(
    wsId: string,
    since: number,
    heldIds: readonly string[],
  ): Promise<TaskBoardChanges | null>;
  currentWorkspaceId(): string | undefined;
  getTasks(): Task[];
  setTasks(tasks: Task[]): void;
}

// A delta re-reads this far behind the last answer, so a write stamped by a clock a
// little behind the relay's (a daemon's) is not skipped. Rows re-sent are merged by id.
export const DELTA_OVERLAP_MS = 120_000;

// The board is read body-trimmed (`description` / `result` null); the task page loads the body
// and patches it onto the same row. A list row must never wipe that body: while the task has
// not changed (same `updatedAt`), a trimmed row keeps the body the held row already carries.
export function keepLoadedBody(held: Task | undefined, row: Task): Task {
  const trimmed = row.description == null && row.result == null;
  if (!trimmed || !held || held.updatedAt !== row.updatedAt) return row;
  if (held.description == null && held.result == null) return row;
  return { ...row, description: held.description, result: held.result };
}

/** A whole-board answer, with the bodies the held rows had already loaded. */
export function keepLoadedBodies(held: readonly Task[], rows: readonly Task[]): Task[] {
  const byId = new Map(held.map((t) => [t.id, t]));
  return rows.map((row) => keepLoadedBody(byId.get(row.id), row));
}

// Apply a delta: changed rows merge onto the live row (identity kept for keyed lists),
// new rows go first, and a row the relay no longer lists is dropped unless it changed
// after the delta was read (a live create that raced the answer).
export function mergeTaskChanges(current: Task[], changes: TaskBoardChanges): Task[] {
  const visible = new Set(changes.ids);
  const changed = new Map(changes.tasks.map((t) => [t.id, t]));
  const kept: Task[] = [];
  for (const task of current) {
    const fresh = changed.get(task.id);
    if (fresh) {
      Object.assign(task, keepLoadedBody(task, fresh));
      changed.delete(task.id);
      kept.push(task);
    } else if (visible.has(task.id) || task.updatedAt > changes.asOf) {
      kept.push(task);
    }
  }
  return [...changed.values(), ...kept];
}

export class TaskBoardSync {
  private loaded: { wsId: string; asOf: number | null } | null = null;
  private inFlight: { wsId: string; promise: Promise<void> } | null = null;
  // Bumped by reset(): an answer from before a workspace switch or logout is dropped.
  private generation = 0;

  constructor(private readonly deps: TaskBoardSyncDeps) {}

  reset(): void {
    this.generation += 1;
    this.loaded = null;
    this.inFlight = null;
  }

  // The whole board, in one call. Joins a load already running for this workspace.
  load(wsId: string): Promise<void> {
    return this.run(wsId, (gen) => this.full(wsId, gen));
  }

  // Bring the board up to date: a delta once it is loaded, the whole board before.
  refresh(wsId: string): Promise<void> {
    return this.run(wsId, (gen) => {
      const asOf = this.loaded?.wsId === wsId ? this.loaded.asOf : null;
      return asOf === null ? this.full(wsId, gen) : this.delta(wsId, asOf, gen);
    });
  }

  private run(wsId: string, work: (gen: number) => Promise<void>): Promise<void> {
    if (this.inFlight?.wsId === wsId) return this.inFlight.promise;
    const promise = work(this.generation).finally(() => {
      if (this.inFlight?.promise === promise) this.inFlight = null;
    });
    this.inFlight = { wsId, promise };
    return promise;
  }

  private current(wsId: string, gen: number): boolean {
    return gen === this.generation && this.deps.currentWorkspaceId() === wsId;
  }

  private async full(wsId: string, gen: number): Promise<void> {
    const { tasks, asOf } = await this.deps.fetchBoard(wsId, (soFar) => {
      if (this.current(wsId, gen))
        this.deps.setTasks(keepLoadedBodies(this.deps.getTasks(), soFar));
    });
    if (!this.current(wsId, gen)) return;
    this.deps.setTasks(keepLoadedBodies(this.deps.getTasks(), tasks));
    this.loaded = { wsId, asOf };
  }

  private async delta(wsId: string, asOf: number, gen: number): Promise<void> {
    const heldIds = this.deps.getTasks().map((t) => t.id);
    const changes = await this.deps.fetchChanges(wsId, asOf - DELTA_OVERLAP_MS, heldIds);
    if (!changes) return this.full(wsId, gen);
    if (!this.current(wsId, gen)) return;
    this.deps.setTasks(mergeTaskChanges(this.deps.getTasks(), changes));
    this.loaded = { wsId, asOf: changes.asOf };
  }
}
